import {
  offlineLocalSessionsAtom,
  offlineSyncQueueAtom,
  useOfflinePendingServerDeleteIds,
  type OfflineLocalSessionListItem,
} from '@/atoms/offline-recording';
import { useSessionSearch } from '@/components/layout/Navigation/sessions/hooks/useSessionSearch';
import { useRetrieveAppointmentOrSchedule } from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import { compareSessionsByStatus } from '@/components/ui/session-status/session-status-order';
import {
  consolidatedSelectedDateRangeFilterAtom,
  consolidatedSelectedPatientProfilesFilterAtom,
  consolidatedSelectedStatusesFilterAtom,
  consolidatedSessionsSortByAtom,
  consolidatedSessionsSortOrderAtom,
} from '@/features/consolidatedSessionsList/atoms';
import {
  isLegacyEhrAppointment,
  toExternalAppointmentData,
  type ConsolidatedSession,
} from '@/features/consolidatedSessionsList/types';
import { isEhrAppointmentLinked } from '@/features/consolidatedSessionsList/utils/isEhrAppointmentLinked';
import { useIsOfflineRecordingEnabled } from '@/features/flags';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import type { OfflineSyncItem } from '@/types/offline-recording';
import { backfillQueueServerSessionId } from '@/utils/offline-recording/backfillQueueServerSessionId';
import {
  orderOfflineRowsByCreatedAt,
  withoutPendingServerDeletes,
} from '@heidi/session/contracts';
import { safeToISOString } from '@heidi/utils/dates';
import { useAtomValue } from 'jotai';
import { orderBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';

const resolveOfflineSessionId = (item: OfflineSyncItem): string =>
  item.serverSessionId ?? item.localSessionId;

const buildSyntheticOfflineSession = (
  item: OfflineSyncItem | OfflineLocalSessionListItem,
  sessionId: string
): ConsolidatedSession => {
  return {
    isFromEhr: false,
    session_id: sessionId,
    kinde_gp_id: 'userId' in item ? item.userId : '',
    created_time_local: item.createdAt,
    displayName: '',
    patient_profile: null,
    session_status: null,
    session_name: '',
    session_gist: null,
    updated_at: item.updatedAt,
    ehr_appt_id: null,
    offline_sync_status: 'status' in item ? item.status : undefined,
    offline_local_session_id: item.localSessionId,
    is_offline_synthetic: true,
  };
};

const unionOfflineSessions = (
  sessions: ConsolidatedSession[],
  localSessions: OfflineLocalSessionListItem[],
  queue: OfflineSyncItem[],
  includeSyntheticRows: boolean
): ConsolidatedSession[] => {
  if (localSessions.length === 0 && queue.length === 0) return sessions;

  const localSessionByLocalId = new Map(
    localSessions.map((item) => [item.localSessionId, item])
  );
  const queueBySessionId = new Map<string, OfflineSyncItem>();
  const queuedLocalSessionIds = new Set<string>();
  for (const item of queue) {
    const queueItem = backfillQueueServerSessionId(item, localSessionByLocalId);
    queueBySessionId.set(resolveOfflineSessionId(queueItem), queueItem);
    queuedLocalSessionIds.add(item.localSessionId);
  }

  const existingIds = new Set(
    sessions
      .map((session) => session.session_id)
      .filter((sessionId): sessionId is string => Boolean(sessionId))
  );

  const patched = sessions.map((session) => {
    if (!session.session_id) return session;
    const item = queueBySessionId.get(session.session_id);
    return item
      ? {
          ...session,
          offline_sync_status: item.status,
          offline_local_session_id: item.localSessionId,
        }
      : session;
  });

  if (!includeSyntheticRows) return patched;

  // Collect first, order by datetime, THEN build the rows. Both loops below walk
  // a de-duping map / the local atom in hydration order, which bears no relation
  // to datetime, so prepending them raw hands the list an arbitrary block.
  //
  // This hook re-sorts the whole combined list downstream, so on the default
  // `createdAt` sort the ordering is invisible. It is applied anyway because the
  // other sort keys (`name`, `status`, `updatedAt`) leave every synthetic row
  // tied — they carry `session_name: ''` and `session_status: null` — and a
  // stable sort then renders whatever order arrived here. Being correct only by
  // a downstream accident is how this bug reached three surfaces; the shared
  // comparator is the guarantee.
  const syntheticItems: {
    item: OfflineSyncItem | OfflineLocalSessionListItem;
    sessionId: string;
    createdAt: string;
  }[] = [];
  for (const [id, item] of queueBySessionId) {
    if (existingIds.has(id)) continue;
    syntheticItems.push({ item, sessionId: id, createdAt: item.createdAt });
  }

  for (const localSession of localSessions) {
    if (
      queuedLocalSessionIds.has(localSession.localSessionId) ||
      existingIds.has(localSession.serverSessionId ?? localSession.localSessionId)
    ) {
      continue;
    }
    syntheticItems.push({
      item: localSession,
      sessionId: localSession.serverSessionId ?? localSession.localSessionId,
      createdAt: localSession.createdAt,
    });
  }

  const synthetic = orderOfflineRowsByCreatedAt(syntheticItems).map(
    ({ item, sessionId }) => buildSyntheticOfflineSession(item, sessionId)
  );

  return [...synthetic, ...patched];
};

/**
 * Hook that provides a consolidated list of sessions from all sources:
 * - Heidi sessions (from useSessionSearch)
 * - EHR appointments (legacy)
 * - Integration schedule items (new integrations)
 *
 * Unlike useUpcomingSessionsList, this hook does NOT filter to only future sessions.
 * It returns all sessions sorted by created_time_local in descending order (newest first).
 */
export const useConsolidatedSessionsList = () => {
  const {
    sessionSearchResult,
    sessionsHiddenByQueuedDelete,
    isPendingSessions,
    refetchSessions,
    searchValue,
  } = useSessionSearch();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const offlineLocalSessions = useAtomValue(offlineLocalSessionsAtom);
  const offlineSyncQueue = useAtomValue(offlineSyncQueueAtom);
  // Never gated on `isOfflineRecordingEnabled` — reasoning in
  // `use-session-list-table-data`, which reads the same mirror.
  const pendingServerDeleteIds = useOfflinePendingServerDeleteIds();
  const selectedStatusesFilter = useAtomValue(consolidatedSelectedStatusesFilterAtom);
  const selectedDateRangeFilter = useAtomValue(consolidatedSelectedDateRangeFilterAtom);
  const selectedPatientProfilesFilter = useAtomValue(
    consolidatedSelectedPatientProfilesFilterAtom
  );
  const sortBy = useAtomValue(consolidatedSessionsSortByAtom);
  const sortOrder = useAtomValue(consolidatedSessionsSortOrderAtom);
  const {
    searchResult: ehrSearchResult,
    isPending: isPendingRetrieveAppointmentOrSchedule,
    refetch: refetchAppointmentOrSchedule,
  } = useRetrieveAppointmentOrSchedule();

  // Transform Heidi sessions to ConsolidatedSession format with extended fields
  const heidiSessions = useMemo(() => {
    return (
      sessionSearchResult
        ?.map((session) => {
          if (!session.created_time_local) {
            return null;
          }
          const parsedDate = parseTimeStamp(
            session.created_time_local,
            !session?.ehr_appt_id
          );
          const isoString = safeToISOString(parsedDate);
          if (!isoString) {
            return null;
          }
          const consolidatedSession: ConsolidatedSession = {
            isFromEhr: !!session?.ehr_appt_id,
            session_id: session.session_id,
            kinde_gp_id: session.kinde_gp_id,
            created_time_local: isoString,
            displayName:
              session.patient_profile?.demographic_string ||
              session.patient.demographic_details ||
              session.session_name ||
              '',
            patient_profile: session.patient_profile,
            // Extended fields for consolidated view
            session_status: session.session_status,
            session_name: session.session_name,
            session_gist: session.session_gist,
            ehr_appt_id: session.ehr_appt_id,
            updated_at: session.updated_at,
            patient_profile_appointment_candidates:
              session.patient_profile_appointment_candidates,
          };
          return consolidatedSession;
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) || []
    );
  }, [sessionSearchResult]);

  // The list the LINK check reads — deliberately not the one the list renders.
  //
  // `heidiSessions` is downstream of the queued-delete read filter, so a session
  // deleted offline is already gone from it. Asking `isEhrAppointmentLinked`
  // against that flips a linked appointment to unlinked, and the appointment is
  // then rebuilt below as a row that still carries the deleted session's
  // `session_id` — which `sessionBar` navigates straight into. The server has
  // not accepted the deletion, so "still linked" is the truthful answer; adding
  // the hidden rows back here keeps the appointment suppressed instead of
  // resurfacing it as bookable. Whole rows, not ids: the schedule-item fallback
  // branch matches on `ehr_appt_id`.
  const linkCheckSessions = useMemo(
    () => [...heidiSessions, ...sessionsHiddenByQueuedDelete],
    [heidiSessions, sessionsHiddenByQueuedDelete]
  );

  // Transform EHR appointments to ConsolidatedSession format
  // Filter out appointments that already have a linked Heidi session
  const ehrAppointments = useMemo(() => {
    if (!ehrSearchResult || isReviewingOtherUser) {
      return [];
    }
    return ehrSearchResult
      .map((appointment) => toExternalAppointmentData(appointment))
      .filter(
        (ehrApptData) => !isEhrAppointmentLinked(ehrApptData.data, linkCheckSessions)
      )
      .map((ehrApptData) => {
        if (isLegacyEhrAppointment(ehrApptData)) {
          if (!ehrApptData.data.appointment_date_time) {
            return null;
          }
          const utcAppointmentDate = parseTimeStamp(
            ehrApptData.data.appointment_date_time,
            false
          );
          const isoString = safeToISOString(utcAppointmentDate);
          if (!isoString) {
            return null;
          }
          const consolidatedSession: ConsolidatedSession = {
            isFromEhr: true,
            session_id: ehrApptData.data.session_id,
            kinde_gp_id: ehrApptData.data.user_id,
            created_time_local: isoString,
            displayName:
              `${ehrApptData.data.patient_first_name || ''} ${ehrApptData.data.patient_last_name || ''}`.trim(),
            externalApptData: ehrApptData,
            patient_profile: null,
            // EHR appointments don't have session status yet
            session_status: null,
            session_name: null,
            session_gist: null,
            ehr_appt_id: ehrApptData.data.record_id,
            updated_at: null,
          };
          return consolidatedSession;
        }

        // ScheduleItem (new integrations)
        if (!ehrApptData.data.appointment.start) {
          return null;
        }
        const utcAppointmentDate = parseTimeStamp(
          ehrApptData.data.appointment.start,
          false
        );
        const isoString = safeToISOString(utcAppointmentDate);
        if (!isoString) {
          return null;
        }
        const consolidatedSession: ConsolidatedSession = {
          isFromEhr: true,
          session_id: ehrApptData.data.appointment.session?.session_id,
          kinde_gp_id: null,
          created_time_local: isoString,
          displayName:
            `${ehrApptData.data.patient?.name?.[0]?.given?.join(' ') || ''} ${ehrApptData.data.patient?.name?.[0]?.family || ''}`.trim(),
          externalApptData: ehrApptData,
          patient_profile: null,
          // Schedule items don't have session status yet
          session_status: null,
          session_name: null,
          session_gist: null,
          ehr_appt_id: null,
          updated_at: null,
        };
        return consolidatedSession;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [ehrSearchResult, linkCheckSessions, isReviewingOtherUser]);

  const hasDateRangeFilter = Boolean(
    selectedDateRangeFilter.from && selectedDateRangeFilter.to
  );

  // Combine, filter, and sort all sessions for consolidated view
  const consolidatedSessions: ConsolidatedSession[] = useMemo(() => {
    const includeSyntheticOfflineRows =
      !searchValue.trim() &&
      selectedStatusesFilter.length === 0 &&
      !hasDateRangeFilter &&
      selectedPatientProfilesFilter.length === 0;
    // Applied after this list's own SECOND offline union — `useSessionSearch`
    // upstream already filtered the backend rows, but this union can re-introduce
    // an id. Deliberately NOT applied to `ehrAppointments`: an EHR appointment is
    // an appointment, not a session, so deleting the session must not remove it
    // from the schedule.
    const sessionList = withoutPendingServerDeletes(
      isOfflineRecordingEnabled
        ? unionOfflineSessions(
            heidiSessions,
            offlineLocalSessions,
            offlineSyncQueue,
            includeSyntheticOfflineRows
          )
        : heidiSessions,
      pendingServerDeleteIds
    );
    let filteredSessions = [...sessionList, ...ehrAppointments];

    if (selectedStatusesFilter.length > 0) {
      filteredSessions = filteredSessions.filter((session) => {
        if (!session.session_status) {
          return true;
        }
        return selectedStatusesFilter.includes(session.session_status);
      });
    }

    if (hasDateRangeFilter) {
      const fromDate = selectedDateRangeFilter.from!;
      const toDate = selectedDateRangeFilter.to!;
      filteredSessions = filteredSessions.filter((session) => {
        if (!session.created_time_local) {
          return false;
        }
        const sessionDate = parseTimeStamp(
          session.created_time_local,
          !session.ehr_appt_id
        );
        return sessionDate >= fromDate && sessionDate <= toDate;
      });
    }

    if (selectedPatientProfilesFilter.length > 0) {
      filteredSessions = filteredSessions.filter((session) => {
        const patientProfileId = session.patient_profile?.id;
        if (!patientProfileId) {
          return false;
        }
        return selectedPatientProfilesFilter.includes(patientProfileId);
      });
    }

    if (sortBy === 'status') {
      return [...filteredSessions].sort((a, b) =>
        compareSessionsByStatus(a, b, sortOrder)
      );
    }

    if (sortBy === 'createdAt') {
      return [...filteredSessions].sort((a, b) => {
        const sessionTimeA = parseTimeStamp(
          a.created_time_local,
          !Boolean(a.ehr_appt_id)
        );
        const sessionTimeB = parseTimeStamp(
          b.created_time_local,
          !Boolean(b.ehr_appt_id)
        );
        return sortOrder === 'asc'
          ? sessionTimeA.getTime() - sessionTimeB.getTime()
          : sessionTimeB.getTime() - sessionTimeA.getTime();
      });
    }

    const sortKeyMap = {
      createdAt: 'created_time_local',
      updatedAt: 'updated_at',
      name: 'session_name',
      status: 'session_status',
    } as const;
    const sortKey = sortKeyMap[sortBy];
    return orderBy(filteredSessions, [sortKey], [sortOrder]);
  }, [
    heidiSessions,
    ehrAppointments,
    isOfflineRecordingEnabled,
    offlineLocalSessions,
    offlineSyncQueue,
    pendingServerDeleteIds,
    searchValue,
    selectedStatusesFilter,
    hasDateRangeFilter,
    selectedDateRangeFilter.from,
    selectedDateRangeFilter.to,
    selectedPatientProfilesFilter,
    sortBy,
    sortOrder,
  ]);

  const refetchConsolidatedSessions = useCallback(() => {
    if (integrationEhrProvider && !isReviewingOtherUser) {
      void refetchAppointmentOrSchedule();
    }
    void refetchSessions();
  }, [
    integrationEhrProvider,
    isReviewingOtherUser,
    refetchSessions,
    refetchAppointmentOrSchedule,
  ]);

  const isPending = useMemo(() => {
    return (
      (integrationEhrProvider &&
        !isReviewingOtherUser &&
        isPendingRetrieveAppointmentOrSchedule) ||
      isPendingSessions
    );
  }, [
    integrationEhrProvider,
    isReviewingOtherUser,
    isPendingRetrieveAppointmentOrSchedule,
    isPendingSessions,
  ]);

  return {
    consolidatedSessions,
    ehrSearchResult,
    refetchConsolidatedSessions,
    isPending,
  };
};
